import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, Pencil, Upload, Loader2, Palette, X, Image as ImageIcon } from 'lucide-react';
import api from '../../lib/api';
import { FONT_THEMES, getFontTheme } from '../../lib/fontThemes';
import { FLYER_LAYOUTS } from '../flyer/FlyerCanvas';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HEX_RE = /^#([0-9a-fA-F]{6})$/;
const EMPTY = { name: '', theme_color: '#2F6B57', accent_color: '#C86B4A', font_theme: 'editorial', flyer_layout: 'classic', logo_url: '' };

const logoSrc = (url) => (url ? `${BACKEND}${url}` : '');

const BrandKitForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/brand-kits/logo', fd);
      set('logo_url', r.data.url);
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const submit = () => {
    if (!form.name.trim()) { toast.error('Give your brand kit a name'); return; }
    onSave(form);
  };

  return (
    <div className="rounded-xl border border-border bg-background p-4 space-y-4" data-testid="brand-kit-form">
      <div className="space-y-2">
        <Label>Kit name</Label>
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Summer Series"
          data-testid="brand-kit-name-input" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Primary color</Label>
          <div className="flex gap-2">
            <input type="color" value={HEX_RE.test(form.theme_color) ? form.theme_color : '#2F6B57'}
              onChange={(e) => set('theme_color', e.target.value)}
              className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Primary color" />
            <Input value={form.theme_color} onChange={(e) => set('theme_color', e.target.value)} className="h-11 font-mono text-sm" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Accent color</Label>
          <div className="flex gap-2">
            <input type="color" value={HEX_RE.test(form.accent_color) ? form.accent_color : '#C86B4A'}
              onChange={(e) => set('accent_color', e.target.value)}
              className="h-11 w-12 rounded-lg border border-border bg-card cursor-pointer p-1" aria-label="Accent color" />
            <Input value={form.accent_color} onChange={(e) => set('accent_color', e.target.value)} className="h-11 font-mono text-sm" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Font theme</Label>
          <Select value={form.font_theme} onValueChange={(v) => set('font_theme', v)}>
            <SelectTrigger data-testid="brand-kit-font-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FONT_THEMES.map((t) => (
                <SelectItem key={t.id} value={t.id}><span style={{ fontFamily: t.display }}>{t.name}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Default flyer layout</Label>
          <Select value={form.flyer_layout} onValueChange={(v) => set('flyer_layout', v)}>
            <SelectTrigger data-testid="brand-kit-layout-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FLYER_LAYOUTS.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Logo / monogram <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <div className="flex items-center gap-3">
          {form.logo_url ? (
            <div className="relative h-14 w-14 rounded-lg border border-border bg-card flex items-center justify-center overflow-hidden">
              <img src={logoSrc(form.logo_url)} alt="logo" className="max-h-full max-w-full object-contain" />
              <button type="button" onClick={() => set('logo_url', '')}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center" aria-label="Remove logo">
                <X className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <div className="h-14 w-14 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
          <Button type="button" variant="outline" size="sm" className="gap-2" disabled={uploading}
            onClick={() => fileRef.current?.click()} data-testid="brand-kit-logo-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {form.logo_url ? 'Replace logo' : 'Upload logo'}
          </Button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => uploadLogo(e.target.files?.[0])} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="gap-2" data-testid="brand-kit-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save kit
        </Button>
      </div>
    </div>
  );
};

export const BrandKitManager = () => {
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/brand-kits').then((r) => setKits(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async (data) => {
    setSaving(true);
    try { await api.post('/brand-kits', data); toast.success('Brand kit created'); setCreating(false); load(); }
    catch (e) { toast.error('Could not create kit'); }
    finally { setSaving(false); }
  };
  const update = async (id, data) => {
    setSaving(true);
    try { await api.put(`/brand-kits/${id}`, data); toast.success('Brand kit updated'); setEditingId(null); load(); }
    catch (e) { toast.error('Could not update kit'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/brand-kits/${id}`); toast.success('Brand kit deleted'); load(); }
    catch (e) { toast.error('Could not delete'); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="brand-kits-section">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Palette className="h-[20px] w-[20px] text-primary" /> Brand Kits
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Save a colors + font + layout + logo combo once, then apply it to any event in one click.</p>
        </div>
        {!creating && (
          <Button size="sm" className="gap-2 flex-shrink-0" onClick={() => { setCreating(true); setEditingId(null); }}
            data-testid="new-brand-kit-button">
            <Plus className="h-4 w-4" /> New kit
          </Button>
        )}
      </div>

      <div className="mt-4 space-y-3">
        {creating && (
          <BrandKitForm onSave={create} onCancel={() => setCreating(false)} saving={saving} />
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : kits.length === 0 && !creating ? (
          <p className="text-sm text-muted-foreground" data-testid="brand-kits-empty">No brand kits yet. Create one to reuse your look across events.</p>
        ) : (
          kits.map((k) => (
            editingId === k.id ? (
              <BrandKitForm key={k.id} initial={k} onSave={(data) => update(k.id, data)} onCancel={() => setEditingId(null)} saving={saving} />
            ) : (
              <div key={k.id} className="flex items-center gap-4 rounded-xl border border-border bg-background p-4" data-testid={`brand-kit-${k.id}`}>
                <div className="h-12 w-12 rounded-lg border border-border flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{ background: k.logo_url ? '#fff' : `linear-gradient(135deg, ${k.theme_color}, ${k.accent_color})` }}>
                  {k.logo_url && <img src={logoSrc(k.logo_url)} alt="" className="max-h-full max-w-full object-contain" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{k.name}</div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="inline-block h-3 w-3 rounded-full border border-border" style={{ background: k.theme_color }} />
                    <span className="inline-block h-3 w-3 rounded-full border border-border" style={{ background: k.accent_color }} />
                    <span style={{ fontFamily: getFontTheme(k.font_theme).display }}>{getFontTheme(k.font_theme).name}</span>
                    <span>·</span>
                    <span>{(FLYER_LAYOUTS.find((l) => l.id === k.flyer_layout) || {}).name || k.flyer_layout}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => { setEditingId(k.id); setCreating(false); }} data-testid={`edit-brand-kit-${k.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(k.id)} data-testid={`delete-brand-kit-${k.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
};
